import React, { useState, useCallback, useEffect } from "react";
import { Stack } from "@mantine/core";
import { PageProps } from "@sdflc/react-router-pages";
import dayjs from "dayjs";

import { useEvents } from "@/hooks";
import { useVenuesContext } from "@/contexts";
import { Event, Pagination, EventFilter } from "@/utils";

import { DashboardLayout } from "@/components/layouts";
import {
  AppButton,
  AppToolbar,
  ConfirmModal,
  EmptyState,
  DateRangeSelector,
  DateRangeValue,
  SortConfig,
} from "@/components/generic";
import {
  EventsTable,
  EventFormModal,
  VenueSelector,
  NeedAddVenue,
} from "@/components/features";
import { DEFAULT_PAGE_SIZE } from "@/config";

export const SingleEventsPage: React.FC<PageProps> = (props: PageProps) => {
  // Get selected venue from context
  const {
    loading: venuesLoading,
    selectedVenue,
    setSelectedVenue,
    hasVenues,
  } = useVenuesContext();

  // Selected venue for events
  const [eventVenueId, setEventVenueId] = useState<string | null>(
    selectedVenue?.id || null
  );

  // Date range state
  const [dateRange, setDateRange] = useState<DateRangeValue>({
    startDate: dayjs().format("YYYY-MM-DD"),
    endDate: dayjs().format("YYYY-MM-DD"),
    viewMode: "day",
    displayDate: "",
  });

  // Track if initial load has happened
  const [hasInitialLoad, setHasInitialLoad] = useState(false);

  // Single events hook
  const {
    events,
    loading: loadingEvents,
    loadingMore: loadingMoreEvents,
    hasMore: hasMoreEvents,
    isEmpty: isEmptyEvents,
    sortBy: eventsSortBy,
    isCreating: isCreatingEvent,
    isUpdating: isUpdatingEvent,
    removingEventId,
    selectedEvent,
    list: listEvents,
    loadMore: loadMoreEvents,
    setSortBy: setEventsSortBy,
    create: createEvent,
    update: updateEvent,
    remove: removeEvent,
    setSelectedEvent,
    lastError: eventsLastError,
    clearError: clearEventsError,
  } = useEvents({
    pageSize: DEFAULT_PAGE_SIZE,
    autoLoad: false,
    isAuthenticated: true,
  });

  // Local UI state
  const [eventFormModalOpened, setEventFormModalOpened] =
    useState<boolean>(false);
  const [deleteModalOpened, setDeleteModalOpened] = useState<boolean>(false);
  const [itemToDelete, setItemToDelete] = useState<Event | null>(null);

  // Load single events with specific parameters
  const loadSingleEvents = useCallback(
    (venueId: string, currentDateRange: DateRangeValue) => {
      console.log("Loading single events with date range:", currentDateRange);

      const venueFilter = { venueId: [venueId] };
      const pagination: Pagination = { page: 1, pageSize: 20 };

      let filter: Partial<EventFilter>;

      if (currentDateRange.viewMode === "day") {
        filter = {
          ...venueFilter,
          eventDate: currentDateRange.startDate,
        };
      } else {
        filter = {
          ...venueFilter,
          eventDateStart: currentDateRange.startDate,
          eventDateEnd: currentDateRange.endDate,
        };
      }

      const sortConfig: SortConfig[] = [
        { key: "events.eventDate", direction: "asc" },
      ];
      console.log("Loading single events with filter:", filter);
      listEvents(filter, pagination, sortConfig, true);
    },
    [listEvents]
  );

  // Initial load effect
  useEffect(() => {
    if (eventVenueId && !hasInitialLoad) {
      setHasInitialLoad(true);
      loadSingleEvents(eventVenueId, dateRange);
    }
  }, [eventVenueId, hasInitialLoad, dateRange, loadSingleEvents]);

  // Update event venue ID when selected venue changes
  useEffect(() => {
    if (selectedVenue?.id !== eventVenueId) {
      setEventVenueId(selectedVenue?.id || null);
      setHasInitialLoad(false);
    }
  }, [selectedVenue?.id, eventVenueId]);

  // Handle venue selection
  const handleVenueChange = useCallback(
    (venueId: string | null, venue: any) => {
      setEventVenueId(venueId);
      setSelectedVenue(venue);
      setHasInitialLoad(false);

      if (venueId) {
        loadSingleEvents(venueId, dateRange);
      }
    },
    [setSelectedVenue, dateRange, loadSingleEvents]
  );

  // Handle date range change
  const handleDateRangeChange = useCallback(
    (newDateRange: DateRangeValue) => {
      console.log("Date range changed:", newDateRange);
      setDateRange(newDateRange);
      clearEventsError();

      if (eventVenueId) {
        loadSingleEvents(eventVenueId, newDateRange);
      }
    },
    [clearEventsError, eventVenueId, loadSingleEvents]
  );

  // Handle add event button click
  const handleAddEvent = useCallback(() => {
    setSelectedEvent({
      id: "",
      eventDate: dateRange.startDate,
    });
    setEventFormModalOpened(true);
  }, [dateRange, setSelectedEvent]);

  // Handle duplicate event
  const handleDuplicateEvent = useCallback(
    (event: Event) => {
      setSelectedEvent({ ...event, id: "" });
      setEventFormModalOpened(true);
    },
    [setSelectedEvent]
  );

  // Handle edit single event
  const handleEditEvent = useCallback(
    (event: Event) => {
      setSelectedEvent(event);
      setEventFormModalOpened(true);
    },
    [setSelectedEvent]
  );

  // Handle view event
  const handleViewEvent = useCallback((event: Event) => {
    console.log("View event:", event);
  }, []);

  // Handle view recurring event from single event
  const handleViewRecurringEventFromSingle = useCallback((event: Event) => {
    if (event.recurringEventId) {
      console.log("View recurring event from single:", event.recurringEventId);
      // TODO: Navigate to recurring events page with this ID
    }
  }, []);

  // Handle remove button click
  const handleRemoveEventClick = useCallback((event: Event) => {
    setItemToDelete(event);
    setDeleteModalOpened(true);
  }, []);

  // Handle confirm delete
  const handleConfirmDelete = useCallback(async () => {
    if (itemToDelete?.id) {
      const success = await removeEvent(itemToDelete.id);

      if (success) {
        setDeleteModalOpened(false);
        setItemToDelete(null);
      }
    }
  }, [itemToDelete, removeEvent]);

  // Handle cancel delete
  const handleCancelDelete = useCallback(() => {
    setDeleteModalOpened(false);
    setItemToDelete(null);
  }, []);

  // Handle form modal close
  const handleEventFormModalClose = useCallback(() => {
    setEventFormModalOpened(false);
    setSelectedEvent(null);
    clearEventsError();
  }, [setSelectedEvent, clearEventsError]);

  // Handle refresh - explicit user action
  const handleRefresh = useCallback(() => {
    if (eventVenueId) {
      loadSingleEvents(eventVenueId, dateRange);
    }
  }, [eventVenueId, dateRange, loadSingleEvents]);

  // Handle event creation
  const handleCreateEvent = useCallback(
    async (eventData: any) => {
      const result = await createEvent(eventData);
      if (result) {
        setEventFormModalOpened(false);
        setSelectedEvent(null);
      }
      return result;
    },
    [createEvent, setSelectedEvent]
  );

  // Handle event update
  const handleUpdateEvent = useCallback(
    async (eventId: string, eventData: any) => {
      const result = await updateEvent(eventId, eventData);
      if (result) {
        setEventFormModalOpened(false);
        setSelectedEvent(null);
      }
      return result;
    },
    [updateEvent, setSelectedEvent]
  );

  return (
    <DashboardLayout {...props}>
      <Stack gap="md">
        {/* Venue selector and controls toolbar */}
        <AppToolbar
          preset="page-header"
          sections={[
            {
              id: "venue-selector",
              items: [
                <VenueSelector
                  key="venue-selector"
                  value={eventVenueId}
                  onChange={handleVenueChange}
                  placeholder="Select venue to manage events"
                  style={{ minWidth: 250 }}
                  required
                  disabled={venuesLoading}
                />,
                <AppButton
                  key="add-event"
                  iconName="Plus"
                  onClick={handleAddEvent}
                  disabled={!eventVenueId || isCreatingEvent}
                >
                  Add
                </AppButton>,
                <AppButton
                  key="refresh"
                  variant="secondary"
                  iconName="Refresh"
                  onClick={handleRefresh}
                  disabled={!eventVenueId || loadingEvents}
                >
                  Refresh
                </AppButton>,
              ],
              align: "left",
              gap: "sm",
            },
            {
              id: "date-controls",
              items: [
                <DateRangeSelector
                  key="date-range-selector"
                  value={dateRange}
                  onChange={handleDateRangeChange}
                  disabled={!eventVenueId || loadingEvents}
                />,
              ],
              align: "right",
            },
          ]}
        />

        <EventsTable
          events={events}
          loading={loadingEvents || venuesLoading}
          loadingMore={loadingMoreEvents}
          hasMore={hasMoreEvents}
          sortBy={eventsSortBy}
          onSortChange={setEventsSortBy}
          onLoadMore={loadMoreEvents}
          onDuplicateEvent={handleDuplicateEvent}
          onEditEvent={handleEditEvent}
          onRemoveEvent={handleRemoveEventClick}
          onViewEvent={handleViewEvent}
          onViewRecurringEvent={handleViewRecurringEventFromSingle}
          removingEventId={removingEventId}
          size="md"
        />

        <EmptyState
          display={
            !!selectedVenue?.id &&
            isEmptyEvents &&
            !loadingEvents &&
            !venuesLoading
          }
          title="No events!"
          description={`No events found for ${dateRange.displayDate.toLowerCase()}`}
          actionIcon="Plus"
          actionLabel="Add Event"
          onAction={handleAddEvent}
        />

        {/* Empty state for no venue exist */}
        <NeedAddVenue
          display={!hasVenues && !loadingEvents && !venuesLoading}
          descriptionExtra="Then add rooms in it and then you can start adding events"
        />

        {/* Empty state for no venue selected */}
        <EmptyState
          display={!selectedVenue?.id && hasVenues}
          title="Select a venue"
          description="Choose a venue from the dropdown to view its schedule"
        />
      </Stack>

      {/* Single event form modal */}
      <EventFormModal
        opened={eventFormModalOpened}
        onClose={handleEventFormModalClose}
        event={selectedEvent}
        venueId={eventVenueId || ""}
        onCreateEvent={handleCreateEvent}
        onUpdateEvent={handleUpdateEvent}
        isCreating={isCreatingEvent}
        isUpdating={isUpdatingEvent}
        lastError={eventsLastError}
        onClearError={clearEventsError}
      />

      {/* Delete confirmation modal */}
      <ConfirmModal
        opened={deleteModalOpened}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Single Event"
        message={`Are you sure you want to delete "${itemToDelete?.name}"?`}
        details="This action cannot be undone."
        confirmInProgress={removingEventId === itemToDelete?.id}
        confirmLabel="Delete"
        type="danger"
      />
    </DashboardLayout>
  );
};
